import { getDefaultEventDate } from "./formConstants";

// Duplicating an event is a field-by-field decision, not a deep copy of the doc.
// A duplicate keeps what the event *is* — its name, copy, venue and shape — and
// leaves behind everything that belonged to the one occurrence that already ran:
// its identity, its guests, its price, its completion state and its Meetup link.
//
// Fields absent from the payload below are dropped by construction: the create
// form rebuilds its state through getEventDataObject(), which allowlists the
// fields it knows, so revenue, payout, status, lastHealthCheck, queue state and
// suggestion provenance on the source doc can never ride along into the copy.

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  return null;
};

// The copy opens on the date a brand-new event would default to (21+ days out,
// next Thu/Fri/Sat) but keeps the original's time of day, since a repeat almost
// always runs at its usual hour. The date can't simply carry over: duplicating a
// past event is the main use, and validateEventFields rejects a past date in
// create mode, so the copy would be unsaveable.
export const nextDatePreservingTime = (sourceDate) => {
  const next = getDefaultEventDate();
  const source = toDate(sourceDate);
  if (source) next.setHours(source.getHours(), source.getMinutes(), 0, 0);
  return next;
};

// An idea from the host's plan already carries the date it was planned for, so
// a repeat opens on that date instead of the default slot. Anything else — a
// past date, a Timestamp, junk — falls back, because the create form rejects a
// past date and a Timestamp would not survive the structured clone below.
const futureDateOverride = (value) => {
  if (!(value instanceof Date) || isNaN(value.getTime())) return null;
  return value.getTime() > Date.now() ? value : null;
};

/**
 * Build the router state that pre-fills the "Create Event" form as a copy of an
 * existing event, mirroring buildSuggestionConversionState's handoff shape.
 * `date` optionally opens the copy on a chosen future date.
 */
export const buildEventDuplicateState = (event, { date } = {}) => ({
  duplicateFrom: {
    // What the event is
    name: event.name || "",
    description: event.description || "",
    // The kind of room and a professional room's field (GIS-305). Left out, a
    // professional room would come back as a social one with no field.
    category: event.category ?? null,
    field: event.field ?? null,
    // Photos already living in Firebase Storage are reused by URL rather than
    // re-uploaded (uploadSinglePhoto passes those through untouched), so a copy
    // costs no extra storage. The tradeoff is that the copy's images resolve
    // against the original's storage path.
    photos: event.photos || [],

    // Where it runs
    hostname: event.hostname || "",
    address: event.address || "",

    date: futureDateOverride(date) || nextDatePreservingTime(event.date),

    // How it runs
    eventType: event.eventType,
    maxRound: event.maxRound,
    sessionTime: event.sessionTime,
    delayStart: event.delayStart,
    group: event.group,
    groupCount: event.groupCount,
    size: event.size,
    isPrivate: event.isPrivate,
    showBadgeTableNumber: event.showBadgeTableNumber,
    // The mixer check-in switch and its duration are part of how it runs.
    // Stated outright (not left undefined) so an old source reads as a mixer.
    requireCheckIn: event.requireCheckIn ?? true,
    duration: event.duration ?? null,
    vendorID: event.vendorID,

    // Deliberately reset. Each of these would cause real damage if it leaked
    // through, so the copy states them outright rather than trusting a default.
    //
    // Price is always zeroed: a copy starts free and the host prices it on
    // purpose. Both halves are needed. Leaving priceSchedule undefined is not
    // the same as empty, because getEventDataObject seeds a paid
    // $10/$15/$20/$25 ladder for a missing schedule, so an undefined schedule
    // would hand the host a paid event they never asked for.
    cost: 0,
    priceSchedule: [],
    // A copy is the next occurrence, so it opens live and unfinished no matter
    // what state the source ended up in. The source is usually a completed or
    // switched-off event, which is exactly what a duplicate must not inherit.
    isActive: true,
    isCompleted: false,
    // A Meetup listing belongs to a single occurrence.
    meetupEventId: "",

    // Left out entirely, and why:
    //   id                       — the copy is a new event; a null id is what
    //                              routes handleSubmit down the create path
    //   movers/sitters/rsvp*/    — guests belong to the occurrence that ran
    //     invitedSitters
    //   eventUsers               — the name/photo snapshot of those guests
    //   createdAt                — stamped server-side on create
    //   hostPin                  — which spot the group took is decided on the
    //                              night, not inherited by the next one
    //
    // Keeping those out also keeps this payload free of Firestore Timestamps and
    // other class instances, which matters: it travels as react-router state and
    // is structured-cloned, so a Timestamp would arrive stripped of its prototype
    // and be written back as a plain { seconds, nanoseconds } map. Plain values
    // and Date survive that round trip intact.
  },
});
